import type { RealtimeStateStore } from "./realtimeContext";

// ---------------------------------------------------------------------------
// Realtime gap recovery
//
// The hub does not replay. A push sent while the connection is down is lost,
// and a new connection id has lost its groups. This watcher turns a gap into
// a re-fetch of what realtime keeps fresh:
//
//   * While the hub is not connected, it calls `onResync` every ~15 s (with
//     +/- 20 % jitter), on a visible tab only. A hidden tab that becomes
//     visible calls it at once.
//   * Each time the hub becomes live it calls `onLive` (for example a group
//     re-join). If a gap came first, it then calls `onResync` once, after
//     the `onLive` promise settles.
//   * The first connect of a session, with no gap seen, calls only `onLive`,
//     so a page boot does not fetch twice.
//
// While the hub is connected there is no timer. This watcher does not find a
// push that was dropped on a live connection.
//
// It reads the state store imperatively: no React state, no re-render.
// ---------------------------------------------------------------------------

/**
 * Re-fetch interval while the hub is not connected. It is the same 15 s as the
 * `staleTime` of the queue tallies and worklists.
 */
export const FALLBACK_POLL_MS = 15_000;

export interface RealtimeLifecycleHandlers {
  /**
   * Runs each time the hub becomes live (also once at start when it is already live).
   * Return a promise to delay the resync (for example a group re-join).
   */
  readonly onLive?: () => void | Promise<void>;
  /** Re-fetch what realtime keeps fresh. Cheap and idempotent (callers invalidate query keys). */
  readonly onResync: () => void;
}

export interface RealtimeLifecycleOptions {
  /** Poll interval while the hub is not connected. Default `FALLBACK_POLL_MS`. */
  readonly pollMs?: number;
  /** Jitter source. Each delay is `pollMs * (0.8 + 0.4 * random())`: +/- 20 %. Default `Math.random`. */
  readonly random?: () => number;
  /** Whether the tab is visible. Default: `document.visibilityState === "visible"`. */
  readonly isVisible?: () => boolean;
}

const documentIsVisible = (): boolean => document.visibilityState === "visible";

/**
 * Watches the realtime state store and re-fetches after a gap. Returns the dispose
 * function: it clears the timer, the store subscription and the visibility listener.
 * A promise from `onLive` that settles after dispose does not call `onResync`.
 */
export function watchRealtimeLifecycle(
  store: RealtimeStateStore,
  handlers: RealtimeLifecycleHandlers,
  options: RealtimeLifecycleOptions = {},
): () => void {
  const pollMs = options.pollMs ?? FALLBACK_POLL_MS;
  const random = options.random ?? Math.random;
  const isVisible = options.isVisible ?? documentIsVisible;

  let disposed = false;
  let connected = false;
  // True when a gap happened since the last live moment: the hub left
  // "connected", or a poll or a visibility wake ran. The resync that follows
  // the next live moment clears it. The first connect of a session leaves it false.
  let needsResync = false;
  // Bumped on every connect and every loss. A promise from an older `onLive`
  // call must not trigger a resync.
  let epoch = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const stopPolling = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  const resync = (): void => {
    needsResync = true;
    handlers.onResync();
  };

  // The next tick is armed before the work, so a throw in `onResync` cannot
  // end the chain. A hidden tab skips the work and keeps the cadence.
  const startPolling = (): void => {
    stopPolling();
    timer = setTimeout(
      () => {
        startPolling();
        if (isVisible()) resync();
      },
      pollMs * (0.8 + 0.4 * random()),
    );
  };

  const goLive = async (liveEpoch: number): Promise<void> => {
    try {
      await handlers.onLive?.();
    } catch {
      // `onLive` is best-effort. A throw or a rejection counts as settled.
    }
    // Skip when disposed or when the hub was lost (or flapped) meanwhile.
    if (disposed || epoch !== liveEpoch || !needsResync) return;
    needsResync = false;
    handlers.onResync();
  };

  const onStoreChange = (): void => {
    if (disposed) return;
    const nowConnected = store.getSnapshot() === "connected";
    // "reconnecting" <-> "disconnected" does not change what this watcher does.
    if (nowConnected === connected) return;
    connected = nowConnected;
    epoch += 1;
    if (nowConnected) {
      stopPolling();
      void goLive(epoch);
    } else {
      needsResync = true;
      startPolling();
    }
  };

  const onVisibilityChange = (): void => {
    if (connected || !isVisible()) return;
    startPolling();
    resync();
  };

  const unsubscribe = store.subscribe(onStoreChange);
  document.addEventListener("visibilitychange", onVisibilityChange);

  connected = store.getSnapshot() === "connected";
  if (connected) {
    void goLive(epoch);
  } else {
    startPolling();
  }

  return () => {
    disposed = true;
    unsubscribe();
    stopPolling();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
